import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ResolvedTheme = "dark" | "light";

type ThemeProviderState = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => undefined,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const safeWindow = () => (typeof window === "undefined" ? null : window);

export function ThemeProvider({ children, defaultTheme = "system", storageKey = "vite-ui-theme" }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const w = safeWindow();
    try {
      const stored = w?.localStorage?.getItem(storageKey) as Theme | null;
      return (stored as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const w = safeWindow();
    if (!w) return "light";
    if (theme !== "system") return theme;
    return w.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
  });

  useEffect(() => {
    const w = safeWindow();
    if (!w) return;

    const root = w.document.documentElement;
    const applyTheme = (nextTheme: ResolvedTheme) => {
      root.classList.remove("light", "dark");
      root.classList.add(nextTheme);
      setResolvedTheme(nextTheme);
    };

    const media = w.matchMedia?.("(prefers-color-scheme: dark)");

    if (theme === "system") {
      applyTheme(media?.matches ? "dark" : "light");
      if (!media) return;
      const handleChange = (event: MediaQueryListEvent) => {
        applyTheme(event.matches ? "dark" : "light");
      };
      if (typeof media.addEventListener === "function") {
        media.addEventListener("change", handleChange);
        return () => media.removeEventListener("change", handleChange);
      }
      media.addListener?.(handleChange);
      return () => media.removeListener?.(handleChange);
    }

    applyTheme(theme);
  }, [theme]);

  const setTheme = (t: Theme) => {
    const w = safeWindow();
    try {
      w?.localStorage?.setItem(storageKey, t);
    } catch {
      /* ignore */
    }
    setThemeState(t);
  };

  return <ThemeProviderContext.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = (): ThemeProviderState => {
  const ctx = useContext(ThemeProviderContext);
  if (ctx === undefined) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
